import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mic, Video, CheckCircle, AlertTriangle, Clock, Info, X } from 'lucide-react';
import './ProductionWorkflow.css';

const EMOTION_OPTIONS = [
  { value: 'happy', label: '高兴' },
  { value: 'sad', label: '悲伤' },
  { value: 'angry', label: '愤怒' },
  { value: 'fearful', label: '害怕' },
  { value: 'disgusted', label: '厌恶' },
  { value: 'surprised', label: '惊讶' },
  { value: 'calm', label: '中性' },
  { value: 'fluent', label: '生动' },
  { value: 'whisper', label: '低语' },
];

const ProductionWorkflow = ({
  data,
  onChange,
  onGenerate,
  isGenerating,
  isValid,
  isButtonDisabled,
  selectedAvatar,
  selectedVoice,
  onClearAvatar,
  onClearVoice,
}) => {
  const navigate = useNavigate();
  const [avatarMode, setAvatarMode] = useState(
    data.digitalHuman.virtualmanKey ? 'key' : 'select'
  );
  const [voiceMode, setVoiceMode] = useState(
    data.voice.audioUrl ? 'audio' : 'voice'
  );

  const handleFieldChange = (section, field, value) => {
    onChange(section, { [field]: value });
  };

  const isValidAudioUrl = /^https?:\/\/.+/.test((data.voice.audioUrl || '').trim());
  const step1Done = avatarMode === 'select'
    ? !!selectedAvatar
    : !!data.digitalHuman.virtualmanKey.trim();
  const step2Done = voiceMode === 'voice'
    ? (!!selectedVoice && data.voice.script.length > 0)
    : isValidAudioUrl;

  return (
    <div className="pw-page">

      {/* Steps indicator */}
      <div className="pw-steps">
        <div className="pw-step">
          <div className={`pw-step-circle ${step1Done ? 'pw-step-circle--done' : 'pw-step-circle--active'}`}>
            {step1Done ? <CheckCircle size={13} /> : '1'}
          </div>
          <span className="pw-step-label">选择数字人</span>
        </div>
        <div className={`pw-step-connector ${step1Done ? 'pw-step-connector--done' : ''}`} />

        <div className="pw-step">
          <div className={`pw-step-circle ${step2Done ? 'pw-step-circle--done' : step1Done ? 'pw-step-circle--active' : 'pw-step-circle--pending'}`}>
            {step2Done ? <CheckCircle size={13} /> : '2'}
          </div>
          <span className={`pw-step-label ${!step1Done ? 'pw-step-label--pending' : ''}`}>选择声音</span>
        </div>
        <div className={`pw-step-connector ${step2Done ? 'pw-step-connector--done' : ''}`} />

        <div className="pw-step">
          <div className={`pw-step-circle ${step2Done ? 'pw-step-circle--active' : 'pw-step-circle--pending'}`}>3</div>
          <span className={`pw-step-label ${!step2Done ? 'pw-step-label--pending' : ''}`}>生成视频</span>
        </div>
      </div>

      {/* Video Title */}
      <div className="pw-title-group">
        <div className="pw-form-group">
          <label className="pw-form-label">
            视频标题
            <span className="pw-tag pw-tag--optional">可选</span>
          </label>
          <input
            type="text"
            className="pw-form-input"
            placeholder="为视频添加一个标题（例如：产品介绍、课程名称等）"
            value={data.voice.title || ''}
            onChange={(e) => handleFieldChange('voice', 'title', e.target.value)}
            maxLength={100}
          />
          <div className="pw-form-footer">
            <span className="pw-form-help">方便在"我的视频"页面中识别和管理</span>
            <span className="pw-form-count">{(data.voice.title || '').length} / 100</span>
          </div>
        </div>
      </div>

      <div className="pw-grid">

        {/* Step 1: Select Digital Human */}
        <div className="pw-card">
          <div className="pw-card-header">
            <div className={`pw-badge ${step1Done ? 'pw-badge--done' : 'pw-badge--active'}`}>
              {step1Done ? <CheckCircle size={12} /> : '1'}
            </div>
            <div>
              <p className="pw-card-title">选择数字人</p>
              <p className="pw-card-desc">选择您的数字人形象</p>
            </div>
          </div>
          <div className="pw-card-body">
            <div className="pw-radio-group">

              {/* Option A: Select from library */}
              <div className={`pw-radio-option ${avatarMode === 'select' ? 'pw-radio-option--active' : ''}`}>
                <div className="pw-radio-header" onClick={() => setAvatarMode('select')}>
                  <span className="pw-radio-dot" />
                  <span className="pw-radio-label">从数字人库中选择</span>
                </div>
                {avatarMode === 'select' && (
                  <div className="pw-radio-body">
                    {selectedAvatar ? (
                      <div className="pw-selected" style={{ alignItems: 'flex-start' }} onClick={() => navigate('/my-avatars')}>
                        <button
                          className="pw-selected-clear"
                          title="取消选择"
                          onClick={(e) => { e.stopPropagation(); onClearAvatar?.(); }}
                        >
                          <X size={12} />
                        </button>
                        <div className="pw-selected-thumb pw-selected-thumb--lg">
                          {selectedAvatar.thumbnail
                            ? <img src={selectedAvatar.thumbnail} alt={selectedAvatar.name} />
                            : <User size={22} className="pw-selected-thumb-icon" />}
                        </div>
                        <div className="pw-selected-info">
                          <p className="pw-selected-name">{selectedAvatar.name}</p>
                          <p className="pw-selected-key">ID: {selectedAvatar.key}</p>
                          <span className="pw-tag pw-tag--success">已选择</span>
                        </div>
                      </div>
                    ) : (
                      <div className="pw-empty">
                        <div className="pw-empty-icon"><User size={22} /></div>
                        <p className="pw-empty-title">选择数字人形象</p>
                        <p className="pw-empty-desc">从您的数字人库中选择，或创建新的数字人</p>
                        <div className="pw-empty-actions">
                          <button className="pw-btn pw-btn--primary" onClick={() => navigate('/my-avatars')}>
                            选择数字人
                          </button>
                          <button className="pw-btn pw-btn--secondary" onClick={() => navigate('/my-avatars', { state: { tab: 'upload' } })}>
                            创建新的
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Option B: Input VirtualmanKey directly */}
              <div className={`pw-radio-option ${avatarMode === 'key' ? 'pw-radio-option--active' : ''}`}>
                <div className="pw-radio-header" onClick={() => setAvatarMode('key')}>
                  <span className="pw-radio-dot" />
                  <span className="pw-radio-label">直接输入 VirtualmanKey</span>
                </div>
                {avatarMode === 'key' && (
                  <div className="pw-radio-body">
                    <div className="pw-form-group">
                      <div className="pw-input-wrap">
                        <input
                          type="text"
                          className="pw-form-input"
                          placeholder="请输入数字人 VirtualmanKey"
                          value={data.digitalHuman.virtualmanKey}
                          onChange={(e) => handleFieldChange('digitalHuman', 'virtualmanKey', e.target.value)}
                        />
                        {data.digitalHuman.virtualmanKey.trim() && (
                          <span className="pw-input-ok"><CheckCircle size={16} /></span>
                        )}
                      </div>
                      <span className="pw-form-help">如果您有现成的 VirtualmanKey，可直接输入使用</span>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Step 2: Select Voice & Script */}
        <div className="pw-card">
          <div className="pw-card-header">
            <div className={`pw-badge ${step2Done ? 'pw-badge--done' : step1Done ? 'pw-badge--active' : 'pw-badge--pending'}`}>
              {step2Done ? <CheckCircle size={12} /> : '2'}
            </div>
            <div>
              <p className="pw-card-title">选择声音与文案</p>
              <p className="pw-card-desc">选择声音并输入要说的内容</p>
            </div>
          </div>
          <div className="pw-card-body">
            <div className="pw-radio-group">

              {/* Option A: Voice + Script */}
              <div className={`pw-radio-option ${voiceMode === 'voice' ? 'pw-radio-option--active' : ''}`}>
                <div className="pw-radio-header" onClick={() => setVoiceMode('voice')}>
                  <span className="pw-radio-dot" />
                  <span className="pw-radio-label">选择音色 + 输入文案</span>
                </div>
                {voiceMode === 'voice' && (
                  <div className="pw-radio-body">
                    {selectedVoice ? (
                      <div className="pw-selected" onClick={() => navigate('/voice-cloning', { state: { from: '/generate-video' } })}>
                        <button
                          className="pw-selected-clear"
                          title="取消选择"
                          onClick={(e) => { e.stopPropagation(); onClearVoice?.(); }}
                        >
                          <X size={12} />
                        </button>
                        <div className="pw-selected-thumb">
                          {selectedVoice.thumbnail
                            ? <img src={selectedVoice.thumbnail} alt={selectedVoice.name} />
                            : <Mic size={22} className="pw-selected-thumb-icon" />}
                        </div>
                        <div className="pw-selected-info">
                          <p className="pw-selected-name">{selectedVoice.name}</p>
                          <span className="pw-tag pw-tag--success">已选择</span>
                          {selectedVoice.sampleUrl && (
                            <audio controls style={{ width: '100%', maxWidth: 200, height: 28, marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
                              <source src={selectedVoice.sampleUrl} type="audio/mpeg" />
                            </audio>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="pw-empty pw-empty--sm">
                        <div className="pw-empty-icon"><Mic size={22} /></div>
                        <p className="pw-empty-title">选择声音</p>
                        <p className="pw-empty-desc">从声音库中选择，或克隆新声音</p>
                        <div className="pw-empty-actions">
                          <button className="pw-btn pw-btn--primary" onClick={() => navigate('/voice-cloning', { state: { from: '/generate-video' } })}>
                            选择声音
                          </button>
                          <button className="pw-btn pw-btn--secondary" onClick={() => navigate('/voice-cloning', { state: { from: '/generate-video' } })}>
                            克隆新声音
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="pw-form-group">
                      <label className="pw-form-label">
                        输入文案 <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <textarea
                        className="pw-form-textarea"
                        placeholder={"请输入让数字人说的文案内容...\n\n示例：\n大家好，我是您的专属数字人助手。今天为大家介绍我们的最新产品功能..."}
                        value={data.voice.script}
                        onChange={(e) => handleFieldChange('voice', 'script', e.target.value)}
                        rows={6}
                        maxLength={4000}
                      />
                      <div className="pw-form-footer">
                        <span className="pw-form-help">建议50-4000字，内容清晰自然</span>
                        <span className="pw-form-count">{data.voice.script.length}/4000</span>
                      </div>
                    </div>

                    {selectedVoice && selectedVoice.companyId === 14 && (
                      <div className="pw-audio-params">
                        <p className="pw-audio-params-title">音频参数设置（高级音色）</p>
                        <div className="pw-grid-3">
                          <div className="pw-form-group">
                            <label className="pw-form-label">情绪</label>
                            <select
                              className="pw-form-select"
                              value={data.voice.emotion || ''}
                              onChange={(e) => handleFieldChange('voice', 'emotion', e.target.value || undefined)}
                            >
                              <option value="">默认</option>
                              {EMOTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          <div className="pw-form-group">
                            <label className="pw-form-label">音速 (0.5-2.0)</label>
                            <input
                              type="number"
                              className="pw-form-input"
                              min="0.5" max="2.0" step="0.1"
                              placeholder="默认 1.0"
                              value={data.voice.speed || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') { handleFieldChange('voice', 'speed', undefined); }
                                else { const n = parseFloat(v); if (n >= 0.5 && n <= 2.0) handleFieldChange('voice', 'speed', n.toString()); }
                              }}
                            />
                          </div>
                          <div className="pw-form-group">
                            <label className="pw-form-label">音量 (0.1-10.0)</label>
                            <input
                              type="number"
                              className="pw-form-input"
                              min="0.1" max="10.0" step="0.1"
                              placeholder="默认 1.0"
                              value={data.voice.volume || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') { handleFieldChange('voice', 'volume', undefined); }
                                else { const n = parseFloat(v); if (n > 0 && n <= 10.0) handleFieldChange('voice', 'volume', n.toString()); }
                              }}
                            />
                          </div>
                        </div>
                        <p className="pw-audio-params-tip">
                          <Info size={12} />
                          高级音色支持情绪、音速和音量调节，让生成语音更富表现力
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Option B: Audio URL */}
              <div className={`pw-radio-option ${voiceMode === 'audio' ? 'pw-radio-option--active' : ''}`}>
                <div className="pw-radio-header" onClick={() => setVoiceMode('audio')}>
                  <span className="pw-radio-dot" />
                  <span className="pw-radio-label">直接使用音频链接</span>
                </div>
                {voiceMode === 'audio' && (
                  <div className="pw-radio-body">
                    <div className="pw-form-group">
                      <div className="pw-input-wrap">
                        <input
                          type="text"
                          className="pw-form-input"
                          placeholder="请输入音频文件链接（支持 MP3、WAV 格式）"
                          value={data.voice.audioUrl}
                          onChange={(e) => handleFieldChange('voice', 'audioUrl', e.target.value)}
                        />
                        {isValidAudioUrl && (
                          <span className="pw-input-ok"><CheckCircle size={16} /></span>
                        )}
                      </div>
                      <span className="pw-form-help">支持 MP3、WAV 等格式的公网可访问链接；使用音频链接时，上方选择的音色和文案不生效</span>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Generate section */}
      <div className="pw-generate">
        <div className="pw-generate-card">
          {isGenerating && (
            <div className="pw-generate-progress">
              <div className="pw-spinner" />
              正在生成视频，请稍候...
            </div>
          )}

          <button className="pw-generate-btn" onClick={onGenerate} disabled={isButtonDisabled}>
            {isGenerating
              ? <><div className="pw-spinner pw-spinner--white" />生成中，请稍候...</>
              : <><Video size={15} />开始生成数字人视频</>
            }
          </button>

          <div className="pw-generate-status">
            {!isValid ? (
              <span className="pw-generate-status--warn">
                <AlertTriangle size={13} />请完成所有必填项：数字人、声音、文案
              </span>
            ) : isButtonDisabled && !isGenerating ? (
              <span className="pw-generate-status--pending">
                <Clock size={13} />任务已提交，正在处理中...
              </span>
            ) : !isButtonDisabled ? (
              <span className="pw-generate-status--ok">
                <CheckCircle size={13} />所有设置已完成，可以开始生成
              </span>
            ) : (
              <span className="pw-generate-status--pending">预计生成时间：2-5分钟，请耐心等待...</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionWorkflow;
